// DQRuleTransformationEditor.tsx

import React, { useState, useEffect } from "react";
import { Button, Form, InputGroup, Modal } from "react-bootstrap";

type TransformExpr = {
  fn: string;
  args?: any[];
  target?: string;
};

type Props = {
  transform: TransformExpr | null | undefined;
  onChange: (updated: TransformExpr | null) => void;
  readOnly?: boolean;
};

// ---------------- Utility ----------------
const safeToString = (v: any): string => {
  if (v === undefined || v === null) return "";
  if (typeof v === "string") return v;
  try {
    return String(v);
  } catch {
    return "";
  }
};

const objToEntries = (obj: any): [string, string][] => {
  if (!obj || typeof obj !== "object") return [];
  return Object.entries(obj).map(([k, v]) => [String(k), safeToString(v)] as [string, string]);
};

const entriesToObj = (entries: [string, string][]) =>
  entries.reduce<Record<string, string>>((acc, [k, v]) => {
    if (k !== "") acc[k] = v;
    return acc;
  }, {});

// ---------------- Object Mapping Editor ----------------
const ObjectMappingEditor: React.FC<{
  field: string;
  entries: [string, string][];
  onFieldChange: (v: string) => void;
  onEntriesChange: (v: [string, string][]) => void;
  readOnly?: boolean;
}> = ({ field, entries, onFieldChange, onEntriesChange, readOnly }) => {
  const handleEntryChange = (idx: number, key: string, val: string) => {
    const next = entries.map((e, i) => (i === idx ? ([key, val] as [string, string]) : e));
    onEntriesChange(next);
  };
  const addEntry = () => onEntriesChange([...entries, ["", ""]]);
  const removeEntry = (idx: number) => onEntriesChange(entries.filter((_, i) => i !== idx));

  return (
    <div>
      <Form.Group className="mb-2">
        <Form.Label size="sm">Field</Form.Label>
        <Form.Control
          size="sm"
          value={field}
          disabled={readOnly}
          onChange={(e) => onFieldChange(e.currentTarget.value)}
        />
      </Form.Group>

      <table className="table table-sm table-borderless">
        <thead>
          <tr>
            <th>From</th>
            <th>To</th>
            {!readOnly && <th />}
          </tr>
        </thead>
        <tbody>
          {entries.map(([from, to], i) => (
            <tr key={i}>
              <td>
                <Form.Control
                  size="sm"
                  value={from}
                  disabled={readOnly}
                  onChange={(e) => handleEntryChange(i, e.currentTarget.value, to)}
                />
              </td>
              <td>
                <Form.Control
                  size="sm"
                  value={to}
                  disabled={readOnly}
                  onChange={(e) => handleEntryChange(i, from, e.currentTarget.value)}
                />
              </td>
              {!readOnly && (
                <td>
                  <Button variant="outline-danger" size="sm" onClick={() => removeEntry(i)}>
                    ✕
                  </Button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      {!readOnly && (
        <Button variant="outline-primary" size="sm" onClick={addEntry}>
          + Add mapping
        </Button>
      )}
    </div>
  );
};

// ---------------- Modal Wrapper ----------------
const ModalWrapper: React.FC<{
  title: string;
  children: React.ReactNode;
  onSave: () => void;
  readOnly?: boolean;
}> = ({ title, children, onSave, readOnly = false }) => {
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleSave = () => {
    onSave();
    setShow(false);
  };

  return (
    <>
      <Button size="sm" variant="outline-primary" onClick={() => setShow(true)} disabled={readOnly}>
        Edit
      </Button>

      <Modal show={show} onHide={handleClose} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{children}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={readOnly}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

// ---------------- Main Editor ----------------
const DQRuleTransformationEditor: React.FC<Props> = ({ transform, onChange, readOnly = false }) => {
  // 1. ALL HOOKS MUST BE DECLARED UNCONDITIONALLY AT THE TOP LEVEL
  // This fixes lines 158-164:37 (useState)
  const [mapField, setMapField] = useState("");
  const [mapEntries, setMapEntries] = useState<[string, string][]>([]);
  const [regexField, setRegexField] = useState("");
  const [regexPattern, setRegexPattern] = useState("");
  const [regexRepl, setRegexRepl] = useState("");
  const [normField, setNormField] = useState("");
  const [normStyle, setNormStyle] = useState("upper");

  // This fixes line 167:3 (useEffect)
  useEffect(() => {
    if (transform) {
      const { fn, args = [] } = transform;
      if (fn === "map_values") {
        setMapField(safeToString(args[0]));
        setMapEntries(objToEntries(args[1]));
      } else if (fn === "regex_replace") {
        setRegexField(safeToString(args[0]));
        setRegexPattern(safeToString(args[1]));
        setRegexRepl(safeToString(args[2]));
      } else if (fn === "normalize_case") {
        setNormField(safeToString(args[0]));
        setNormStyle(safeToString(args[1] || "upper"));
      }
    }
  }, [transform]); // Depend on transform to run logic when it changes/is loaded

  // 2. CONDITIONAL EARLY RETURN MUST COME AFTER ALL HOOKS
  if (!transform) return null;

  const { fn, args = [] } = transform;

  // Inline arg update helper
  const handleArgChange = (i: number, value: string) => {
    const next = [...args];
    next[i] = value;
    onChange({ ...transform, args: next });
  };

  // Conditional UI rendering (after hooks)
  if (fn === "map_values") {
    const handleSave = () => {
      onChange({ ...transform, args: [mapField, entriesToObj(mapEntries)] });
    };
    return (
      <div className="mb-3">
        <strong>Map Values</strong>{" "}
        <small className="text-muted">Apply replacements inside a field</small>
        <ModalWrapper title="Map Values" onSave={handleSave} readOnly={readOnly}>
          <ObjectMappingEditor
            field={mapField}
            entries={mapEntries}
            onFieldChange={setMapField}
            onEntriesChange={setMapEntries}
            readOnly={readOnly}
          />
        </ModalWrapper>
      </div>
    );
  }

  if (fn === "regex_replace") {
    const handleSave = () => {
      onChange({ ...transform, args: [regexField, regexPattern, regexRepl] });
    };
    return (
      <div className="mb-3">
        <strong>Regex Replace</strong>
        <ModalWrapper title="Regex Replace" onSave={handleSave} readOnly={readOnly}>
          <Form.Group className="mb-2">
            <Form.Label size="sm">Field</Form.Label>
            <Form.Control size="sm" value={regexField} onChange={(e) => setRegexField(e.currentTarget.value)} />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label size="sm">Pattern</Form.Label>
            <Form.Control size="sm" value={regexPattern} onChange={(e) => setRegexPattern(e.currentTarget.value)} />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label size="sm">Replacement</Form.Label>
            <Form.Control size="sm" value={regexRepl} onChange={(e) => setRegexRepl(e.currentTarget.value)} />
          </Form.Group>
        </ModalWrapper>
      </div>
    );
  }

  if (fn === "normalize_case") {
    const handleSave = () => {
      onChange({ ...transform, args: [normField, normStyle] });
    };
    return (
      <div className="mb-3">
        <strong>Normalize Case</strong>
        <ModalWrapper title="Normalize Case" onSave={handleSave} readOnly={readOnly}>
          <Form.Group className="mb-2">
            <Form.Label size="sm">Field</Form.Label>
            <Form.Control size="sm" value={normField} onChange={(e) => setNormField(e.currentTarget.value)} />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label size="sm">Style</Form.Label>
            <Form.Select value={normStyle} onChange={(e) => setNormStyle(e.currentTarget.value)}>
              <option value="lower">lower</option>
              <option value="upper">upper</option>
              <option value="title">title</option>
              <option value="sentence">sentence</option>
            </Form.Select>
          </Form.Group>
        </ModalWrapper>
      </div>
    );
  }

  // Inline simple ones
  if (["trim", "upper", "lower"].includes(fn)) {
    const field = safeToString(args[0] ?? "");
    return (
      <div className="mb-3">
        <strong>{fn}</strong>
        <Form.Group className="mb-2">
          <Form.Label size="sm">Field</Form.Label>
          <Form.Control
            size="sm"
            value={field}
            disabled={readOnly}
            onChange={(e) => handleArgChange(0, e.currentTarget.value)}
          />
        </Form.Group>
      </div>
    );
  }

  // Fallback
  return (
    <div className="mb-3">
      <strong>{fn || "Unknown transform"}</strong>
      {args.length ? (
        args.map((a, i) => (
          <Form.Group key={i} className="mb-2">
            <Form.Label size="sm">Arg {i}</Form.Label>
            <Form.Control
              size="sm"
              value={safeToString(a)}
              disabled={readOnly}
              onChange={(e) => handleArgChange(i, e.currentTarget.value)}
            />
          </Form.Group>
        ))
      ) : (
        <div className="text-muted small">No arguments</div>
      )}
    </div>
  );
};

export default DQRuleTransformationEditor;